import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { useIsPortrait } from "./useIsPortrait";
import HTLDSlide7bMobile from "./HTLDSlide7bMobile";
import { useVisibleAnimation } from "./useVisibleAnimation";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { useSlide7b, Seg } from "./slide7b.data";

const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <span key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</span>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const accent = "#c090e0";
const font   = FONT_BODY;

const S: Record<string, CSSProperties> = {
  root: {
    background: "#040f18", fontFamily: font, color: "#d0e8f8",
    width: "100%", height: "100%",
    display: "flex", flexDirection: "column",
    boxSizing: "border-box", overflow: "hidden",
  },
  subtitleBar: {
    background: "#050f1a", borderBottom: "1px solid rgba(192,144,224,0.12)",
    padding: "clamp(5px,0.6vw,9px) clamp(16px,3vw,40px)",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: "clamp(12px,2vw,28px)", flexShrink: 0,
  },
  body: {
    flex: 1, minHeight: 0,
    display: "flex", gap: "clamp(16px,2vw,28px)",
    padding: "clamp(10px,1.4vw,18px) clamp(16px,3vw,40px) clamp(10px,1.2vw,14px)",
    alignItems: "stretch", overflow: "hidden",
  },
  content: {
    flex: 1, minWidth: "min(100%, 380px)",
    display: "flex", flexDirection: "column",
    justifyContent: "space-between",
  },
  panel: {
    width: "clamp(220px,28vw,380px)", flexShrink: 0,
    display: "flex", flexDirection: "column", alignItems: "center",
    overflow: "hidden",
  },
};

// ─── Threshold Canvas ──────────────────────────────────────────────────────────
// Reuses the Yoga Nidra anatomical figure. The body settles into darkness from the
// feet upward (deepening rest), while a single disciplined light holds at the crown
// (awareness, awake). Minimal: a vertical standing silhouette, a slow settle, one
// steady crown glow. Mostly negative space.
const ThresholdCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
const drawFnRef = useRef<null | (() => void)>(null);
const dtRef     = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; if (!ctx) return;
    const W = 400, H = 560; canvas.width = W; canvas.height = H;

    let frame = 0;

    // ── Yoga Nidra body paths (same source art, so the figure matches slide 11) ──
    interface Def { left: number; top: number; w: number; h: number; path: string; }
    const DEFS: Record<string, Def> = {
      head:     { left: 75,   top: -6,  w: 56.594,  h: 95.031,  path: "M15.92 68.5l8.8 12.546 3.97 13.984-9.254-7.38-4.622-15.848zm27.1 0l-8.8 12.546-3.976 13.988 9.254-7.38 4.622-15.848zm6.11-27.775l.108-11.775-21.16-14.742L8.123 26.133 8.09 40.19l-3.24.215 1.462 9.732 5.208 1.81 2.36 11.63 9.72 11.018 10.856-.324 9.56-10.37 1.918-11.952 5.207-1.81 1.342-9.517zm-43.085-1.84l-.257-13.82L28.226 11.9l23.618 15.755-.216 10.37 4.976-17.085L42.556 2.376 25.49 0 10.803 3.673.002 24.415z" },
      shoulder: { left: 50,   top: 69,  w: 109.532, h: 46.594,  path: "M38.244-.004l1.98 9.232-11.653 2.857-7.474-2.637zm33.032 0l-1.98 9.232 11.653 2.857 7.474-2.637zm21.238 10.54l4.044-2.187 12.656 14 .07 5.33S92.76 10.66 92.515 10.535zm-1.285.58c-.008.28 17.762 18.922 17.762 18.922l.537 16.557-6.157-10.55L91.5 30.988 83.148 15.6zm-74.224-.58L12.962 8.35l-12.656 14-.062 5.325s16.52-17.015 16.764-17.14zm1.285.58C18.3 11.396.528 30.038.528 30.038L-.01 46.595l6.157-10.55 11.87-5.056L26.374 15.6z" },
      arm:      { left: 25.5, top: 112, w: 156.344, h: 119.25,  path: "M21.12 56.5a1.678 1.678 0 0 1-.427.33l.935 8.224 12.977-13.89 1.2-8.958A168.2 168.2 0 0 0 21.12 56.5zm1.387 12.522l-18.07 48.91 5.757 1.333 19.125-39.44 3.518-22.047zm-5.278-18.96l2.638 18.74-17.2 46.023L.01 113.05l6.644-35.518zm118.015 6.44a1.678 1.678 0 0 0 .426.33l-.934 8.222-12.977-13.89-1.2-8.958A168.2 168.2 0 0 1 135.24 56.5zm-1.39 12.52l18.073 48.91-5.758 1.333-19.132-39.44-3.52-22.05zm5.28-18.96l-2.64 18.74 17.2 46.023 2.658-1.775-6.643-35.518zm-103.1-12.323a1.78 1.78 0 0 1 .407-.24l3.666-27.345L33.07.015l-7.258 10.58-6.16 37.04.566 4.973a151.447 151.447 0 0 1 15.808-14.87zm84.3 0a1.824 1.824 0 0 0-.407-.24l-3.666-27.345L123.3.015l7.258 10.58 6.16 37.04-.566 4.973a151.447 151.447 0 0 0-15.822-14.87zM22.288 8.832l-3.3 35.276-2.2-26.238zm111.79 0l3.3 35.276 2.2-26.238z" },
      chest:    { left: 60,   top: 88,  w: 86.594,  h: 45.063,  path: "M19.32 0l-9.225 16.488-10.1 5.056 6.15 4.836 4.832 14.07 11.2 4.616 17.85-8.828-4.452-34.7zm47.934 0l9.225 16.488 10.1 5.056-6.15 4.836-4.833 14.07-11.2 4.616-17.844-8.828 4.45-34.7z" },
      stomach:  { left: 66,   top: 130, w: 75.25,   h: 107.594, path: "M19.25 7.49l16.6-7.5-.5 12.16-14.943 7.662zm-10.322 8.9l6.9 3.848-.8-9.116zm5.617-8.732L1.32 2.15 6.3 15.6zm-8.17 9.267l9.015 5.514 1.54 11.028-8.795-5.735zm15.53 5.89l.332 8.662 12.286-2.665.664-11.826zm14.61 84.783L33.28 76.062l-.08-20.53-11.654-5.736-1.32 37.5zM22.735 35.64L22.57 46.3l11.787 3.166.166-16.657zm-14.16-5.255L16.49 35.9l1.1 11.25-8.8-7.06zm8.79 22.74l-9.673-7.28-.84 9.78L-.006 68.29l10.564 14.594 5.5.883 1.98-20.735zM56 7.488l-16.6-7.5-.5 12.16 14.942 7.66zm10.32 8.9l-6.9 3.847.8-9.116zm-5.617-8.733L73.93 2.148l-4.98 13.447zm8.17 9.267l-9.015 5.514-1.54 11.03 8.8-5.736zm-15.53 5.89l-.332 8.662-12.285-2.665-.664-11.827zm-14.61 84.783l3.234-31.536.082-20.532 11.65-5.735 1.32 37.5zm13.78-71.957l.166 10.66-11.786 3.168-.166-16.657zm14.16-5.256l-7.915 5.514-1.1 11.25 8.794-7.06zm-8.79 22.743l9.673-7.28.84 9.78 6.862 12.66-10.564 14.597-5.5.883-1.975-20.74z" },
      legs:     { left: 57,   top: 205, w: 93.626,  h: 286.625, path: "M17.143 138.643l-.664 5.99 4.647 5.77 1.55 9.1 3.1 1.33 2.655-13.755 1.77-4.88-1.55-3.107zm20.582.444l-3.32 9.318-7.082 13.755 1.77 12.647 5.09-14.2 4.205-7.982zm-26.557-12.645l5.09 27.29-3.32-1.777-2.656 8.875zm22.795 42.374l-1.55 4.88-3.32 20.634-.442 27.51 4.65 26.847-.223-34.39 4.87-13.754.663-15.087zM23.34 181.24l1.106 41.267 8.853 33.28-9.628-4.55-16.045-57.8 5.533-36.384zm15.934 80.536l-.664 18.415-1.55 6.435h-4.647l-1.327-4.437-1.55-.222.332 4.437-5.864-1.778-1.55-.887-6.64-1.442-.22-5.214 6.418-10.87 4.426-5.548 10.844-4.437zM13.63 3.076v22.476l15.71 31.073 9.923 30.85L38.23 66.1zm25.49 30.248l.118-.148-.793-2.024L21.9 12.992l-1.242-.44L31.642 40.93zM32.865 44.09l6.812 17.6 2.274-21.596-1.344-3.43zM6.395 61.91l.827 25.34 12.816 35.257-3.928 10.136L3.5 88.133zM30.96 74.69l.345.826 6.47 15.48-4.177 38.342-6.594-3.526 5.715-35.7zm45.5 63.953l.663 5.99-4.647 5.77-1.55 9.1-3.1 1.33-2.655-13.755 1.77-4.88 1.55-3.107zm-20.582.444l3.32 9.318 7.08 13.755-1.77 12.647-5.09-14.2-4.2-7.987zm3.762 29.73l1.55 4.88 3.32 20.633.442 27.51-4.648 26.847.22-34.39-4.867-13.754-.67-15.087zm10.623 12.424l-1.107 41.267-8.852 33.28 9.627-4.55 16.046-57.8-5.533-36.384zM54.33 261.777l.663 18.415 1.546 6.435h4.648l1.328-4.437 1.55-.222-.333 4.437 5.863-1.778 1.55-.887 6.638-1.442.222-5.214-6.418-10.868-4.426-5.547-10.844-4.437zm25.643-258.7v22.476L64.26 56.625l-9.923 30.85L55.37 66.1zM54.48 33.326l-.118-.15.793-2.023L71.7 12.993l1.24-.44L61.96 40.93zm6.255 10.764l-6.812 17.6-2.274-21.595 1.344-3.43zm26.47 17.82l-.827 25.342-12.816 35.256 3.927 10.136 12.61-44.51zM62.64 74.693l-.346.825-6.47 15.48 4.178 38.342 6.594-3.527-5.715-35.7zm19.792 51.75l-5.09 27.29 3.32-1.776 2.655 8.875zM9.495-.007l.827 21.373-7.028 42.308-3.306-34.155zm2.068 27.323L26.24 59.707l3.307 26-6.2 36.58L9.91 85.046l-.827-38.342zM84.103-.01l-.826 21.375 7.03 42.308 3.306-34.155zm-2.066 27.325L67.36 59.707l-3.308 26 6.2 36.58 13.436-37.24.827-38.34z" },
      hands:    { left: 1,    top: 224, w: 205,     h: 38.938,  path: "M21.255-.002l2.88 6.9 8.412 1.335.664 12.458-4.427 17.8-2.878-.22 2.8-11.847-2.99-.084-4.676 12.6-3.544-.446 4.4-12.736-3.072-.584-5.978 13.543-4.428-.445 6.088-14.1-2.1-1.25-7.528 12.012-3.764-.445L12.4 12.9l-1.107-1.78L.665 15.57 0 13.124l8.635-7.786zm162.49 0l-2.88 6.9-8.412 1.335-.664 12.458 4.427 17.8-2.878-.22-2.8-11.847 2.99-.084 4.676 12.6 3.544-.446-4.4-12.736 3.072-.584 5.978 13.543 4.428-.445-6.088-14.1 2.1-1.25 7.528 12.012 3.764-.445L192.6 12.9l1.107-1.78 10.628 4.45.665-2.447-8.635-7.786z" },
    };

    // Layout: standing figure, centred, occupying the vertical middle. Crown near top.
    const cx = 200;
    const SCALE = 0.86;
    const BODY_X = cx - 103.5 * SCALE;
    const BODY_Y = 96;                       // top of the head
    const CROWN_X = cx, CROWN_Y = BODY_Y + 6;

    // Draw order, head → feet.
    const ORDER = ["head", "shoulder", "chest", "arm", "hands", "stomach", "legs"];

    const stars = [[26,40,0.9],[64,18,0.6],[330,30,0.9],[372,64,0.6],[300,470,0.7],[70,500,0.8],[356,420,0.7]];

    function drawPart(key: string, fill: string, stroke: string, lw: number, shadowC?: string, shadowB?: number, clipY?: [number, number]) {
      const def = DEFS[key];
      const path = new Path2D(def.path);
      ctx.save();
      ctx.translate(BODY_X + def.left * SCALE, BODY_Y + def.top * SCALE);
      ctx.scale(SCALE, SCALE);
      if (clipY) {
        // clip in the part's own local (pre-scale) coordinate space
        ctx.beginPath();
        ctx.rect(-4, clipY[0] - 2, def.w + 8, (clipY[1] - clipY[0]) + 4);
        ctx.clip();
      }
      if (shadowC) { ctx.shadowColor = shadowC; ctx.shadowBlur = (shadowB || 0) / SCALE; }
      ctx.fillStyle = fill; ctx.fill(path);
      ctx.shadowBlur = 0;
      ctx.strokeStyle = stroke; ctx.lineWidth = lw / SCALE; ctx.stroke(path);
      ctx.restore();
    }

    function drawBg() {
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = "#040f18"; ctx.fillRect(0, 0, W, H);
      stars.forEach(([x, y, r]) => {
        const tw = 0.4 + 0.6 * Math.abs(Math.sin(frame * 0.015 + x));
        ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${(0.4 * tw).toFixed(2)})`; ctx.fill();
      });
    }

    function draw() {
      drawBg();

      // One slow shared breath drives both the body outline and the head rings.
      const breath = 0.5 + 0.5 * Math.sin(frame * 0.03);   // 0..1, ~3.5s in/out

      // The whole body OUTLINE breathes uniformly — the stroke brightens and its
      // soft glow swells on the in-breath, eases on the out. No part-by-part sequencing.
      const bodyStroke = 0.20 + 0.26 * breath;             // outline opacity breathes
      const bodyShadow = 6 + 9 * breath;                   // glow blur breathes
      ORDER.forEach(key => {
        drawPart(
          key,
          "rgba(192,144,224,0.05)",
          `rgba(205,168,255,${bodyStroke.toFixed(3)})`,
          0.8,
          `rgba(192,144,224,0.40)`,
          bodyShadow,
        );
      });

      // ── The head: full glow + concentric breathing rings expanding outward ──
      // Concentric rings ripple out from the crown on the breath rhythm.
      const RINGS = 4;
      // Ring reach breathes: the rings push further out on the in-breath, draw in on the out.
      const ringSpan = 60 + 55 * breath;     // max radius swells & contracts with the breath
      for (let i = 0; i < RINGS; i++) {
        const rp = ((frame * 0.5 + i * (100 / RINGS)) % 100) / 100;  // 0..1, staggered
        const rr = 18 + rp * ringSpan;                               // expand, scaled by the breath
        const ra = (1 - rp) * (0.20 + 0.26 * breath);                // brighter on the in-breath
        ctx.beginPath(); ctx.arc(CROWN_X, CROWN_Y, rr, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(232,214,255,${ra.toFixed(3)})`;
        ctx.lineWidth = 1.4 + 0.9 * breath;     // thicker on the in-breath
        ctx.shadowColor = "rgba(210,168,255,0.6)";
        ctx.shadowBlur = 4 + 5 * breath;        // glow swells on the in-breath
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      // Full head glow halo, breathing
      const haloR = 34 + 8 * breath;
      const halo = ctx.createRadialGradient(CROWN_X, CROWN_Y, 2, CROWN_X, CROWN_Y, haloR);
      halo.addColorStop(0,   `rgba(232,214,255,${(0.34 + 0.18 * breath).toFixed(3)})`);
      halo.addColorStop(0.55,`rgba(192,144,224,${(0.12 + 0.07 * breath).toFixed(3)})`);
      halo.addColorStop(1,   "rgba(192,144,224,0)");
      ctx.beginPath(); ctx.arc(CROWN_X, CROWN_Y, haloR, 0, Math.PI * 2);
      ctx.fillStyle = halo; ctx.fill();

      // The head itself — lit and glowing on the breath, CLIPPED to exclude the neck.
      // The head path runs ~0..64 (skull/face); the neck sits ~65..95, so clip [0,64].
      drawPart(
        "head",
        `rgba(232,214,255,${(0.12 + 0.08 * breath).toFixed(3)})`,
        `rgba(242,224,255,${(0.66 + 0.18 * breath).toFixed(3)})`,
        1.1,
        "rgba(210,168,255,0.7)",
        12 + 10 * breath,
        [0, 64],
      );

      // a steady core point at the crown — the witness
      const core = ctx.createRadialGradient(CROWN_X, CROWN_Y, 0, CROWN_X, CROWN_Y, 6);
      core.addColorStop(0, "rgba(255,252,255,0.95)");
      core.addColorStop(1, "rgba(232,214,255,0)");
      ctx.beginPath(); ctx.arc(CROWN_X, CROWN_Y, 6, 0, Math.PI * 2);
      ctx.fillStyle = core; ctx.fill();

frame += dtRef.current > 0 ? dtRef.current * 60 : 1;
    }
    drawFnRef.current = draw;

    return () => { drawFnRef.current = null; };
  }, []);

useVisibleAnimation(canvasRef, (dt) => { dtRef.current = dt; drawFnRef.current?.(); }, { fps: 30 });

  return (
    <canvas
      ref={canvasRef}
      style={{ display: "block", borderRadius: 8, width: "100%", height: "100%", objectFit: "contain" }}
    />
  );
};

// ─── Slide content ─────────────────────────────────────────────────────────────
const MindAndBodyInner: React.FC = () => {
  const { meta, still, awareness, watch } = useSlide7b();
  const fontLinkRef = useRef<HTMLLinkElement | null>(null);

  useEffect(() => {
    const existing = document.querySelector('link[href*="Space+Grotesk"]') as HTMLLinkElement | null;
    if (!existing) {
      const link = document.createElement("link");
      link.rel  = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(link);
      fontLinkRef.current = link;
    }
    return () => {
      if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
        document.head.removeChild(fontLinkRef.current); fontLinkRef.current = null;
      }
    };
  }, []);

  const lbl: CSSProperties = {
    fontSize: "clamp(8px,0.7vw,11px)", fontFamily: FONT_MONO,
    color: accent, letterSpacing: 3, textTransform: "uppercase",
    marginBottom: "clamp(4px,0.55vw,8px)", fontStyle: "normal",
  };
  const txt: CSSProperties = {
    fontSize: "clamp(12px,1.0vw,15px)", fontStyle: "normal", fontFamily: FONT_BODY,
    lineHeight: 1.7, color: "#cde0f2", margin: 0,
  };

  const rootRef = useRef<HTMLDivElement>(null);

  return (
    <div style={S.root} ref={rootRef}>
      <SideStarField rootRef={rootRef} />

      {/* Header row */}
      <div style={S.subtitleBar}>
        <div style={{
          fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal", color: "#7aa8cc",
          fontFamily: FONT_BODY,
        }}>
          {meta.breadcrumbPre} &nbsp;·&nbsp;{" "}
          <span style={{ color: accent }}>{meta.breadcrumbAccent}</span>
        </div>
        <div style={{
          fontSize: "clamp(16px,1.9vw,28px)", fontStyle: "normal", fontWeight: 600,
          color: "#e8f4ff", fontFamily: FONT_DISPLAY, letterSpacing: 0.5, lineHeight: 1,
          whiteSpace: "nowrap",
        }}>
          {meta.headerTitlePlain}<span style={{ color: accent }}>{meta.headerTitleAccent}</span>
        </div>
      </div>

      <div style={S.body}>
        <div style={S.content}>

          <div>
            {/* <div style={{ fontSize: "clamp(8px,0.68vw,11px)", fontFamily: "'Courier New',monospace", color: "rgba(192,144,224,0.55)", letterSpacing: 3, marginBottom: "clamp(4px,0.5vw,7px)" }}>
              मन और शरीर
            </div> */}
            <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(19px,2.3vw,30px)", fontStyle: "normal", fontWeight: 600, color: "#e8f4ff", margin: "0 0 clamp(2px,0.35vw,6px)", lineHeight: 1.2 }}>
              {meta.titlePre}<span style={{ color: accent }}>{meta.titleAccentWord}</span>{meta.titlePost}
            </h1>
            <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.1vw,15px)", fontStyle: "normal", fontWeight: 300, color: "#9bbdd6", margin: 0, letterSpacing: 0.3 }}>
              {meta.subtitle}
            </p>
          </div>

          <div style={{ borderBottom: "1px solid rgba(192,144,224,0.10)", paddingBottom: "clamp(8px,1vw,14px)" }}>
            <div style={lbl}>{meta.stillLabel}</div>
            <p style={txt}>{renderSegs(still, "#d8c0ff")}</p>
          </div>

          <div style={{ borderBottom: "1px solid rgba(192,144,224,0.10)", paddingBottom: "clamp(8px,1vw,14px)" }}>
            <div style={lbl}>{meta.awarenessLabel}</div>
            <p style={txt}>{renderSegs(awareness, "#d8c0ff")}</p>
          </div>

          <div style={{ background: "rgba(192,144,224,0.06)", border: "1px solid rgba(192,144,224,0.22)", borderRadius: 8, padding: "clamp(11px,1.3vw,18px) clamp(13px,1.5vw,20px)" }}>
            <div style={lbl}>{meta.watchLabel}</div>
            <p style={{ ...txt, color: "#d0b8f0" }}>{renderSegs(watch, "#ffffff")}</p>
          </div>

        </div>

        {/* Panel — threshold visual fills the full panel height */}
        <div style={S.panel}>
          <ThresholdCanvas />
        </div>
      </div>

    </div>
  );
};

const HTLDSlide7b: React.FC = () => {
  const portrait = useIsPortrait();
  return portrait
    ? <HTLDSlide7bMobile />
    : <ScaleToFit><MindAndBodyInner /></ScaleToFit>;
};

export default HTLDSlide7b;
